import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Archive, CalendarRange, Ellipsis, Pencil, Plus, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { photoSrc } from '@/api/queries'
import {
  classTypesQuery,
  resourcesQuery,
  templatesQuery,
  useRetireTemplate,
  type TemplateChange,
  type TemplateRow,
} from '@/api/services'
import { useNow } from '@/app/access/use-now'
import { useCompanyCaller } from '@/app/company'
import { BranchSelect, ServicesShell, type ServicesContext } from '@/app/services/services-shell'
import { SlotPanel, type SlotDone, type SlotOpen } from '@/app/services/slot-panel'
import { hhmm, visibleHours, wallOf, weekdayOf } from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { serviceAbilities } from '@/domain/services'
import { slotErrorText } from '@/domain/service-refusals'
import {
  byWeekday,
  clashPairs,
  endOf,
  isRunning,
  keptSessions,
  referenceWeek,
  reopenable,
  slotClashes,
  slotIdOfItem,
  slotItemId,
  slotParts,
  slotsOfBranch,
  splitSlots,
  startsLater,
} from '@/domain/timetable'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar, CompactRow } from '@/ui/compact-row'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem } from '@/ui/overlays'
import { EmptyState } from '@/ui/page'
import { SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'
import { DateHead, WeekGrid, type GridItem } from '@/ui/week-grid'

type Search = { branch?: string }

const isId = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f-]{36}$/.test(value)

export const Route = createFileRoute('/_app/c/$orgId/services/timetable')({
  validateSearch: (search: Record<string, unknown>): Search => (isId(search['branch']) ? { branch: search['branch'] } : {}),
  component: Timetable,
})

function Timetable() {
  const { orgId } = Route.useParams()
  const { caller } = useCompanyCaller(orgId)
  const manage = serviceAbilities(caller).manageTimetable
  const [open, setOpen] = useState<SlotOpen | null>(null)
  const actions = manage ? (
    <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />} onClick={() => setOpen({ at: null })}>
      {t.services.timetable.add}
    </Button>
  ) : undefined
  return (
    <ServicesShell orgId={orgId} tab="timetable" actions={actions}>
      {(ctx) => <TimetableScreen ctx={ctx} open={open} setOpen={setOpen} />}
    </ServicesShell>
  )
}

/// What the screen says after a save or a retirement: the news, and the
/// sessions the server kept as they were (S9) — the manager decides about them.
type Outcome = Readonly<{ message: string; change?: TemplateChange | undefined; zone: string }>

const dayAndMonth = (iso: string, zone: string): string =>
  new Intl.DateTimeFormat('uk-UA', { timeZone: zone, weekday: 'short', day: 'numeric', month: 'long' }).format(new Date(iso))

const shortDate = (date: string): string =>
  new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(new Date(`${date}T00:00:00Z`))

/// «Розклад»: the club's week of group classes as it repeats — every running
/// slot in a Monday-to-Sunday grid on the club's wall clock (a list by day on
/// a phone), the ended ones apart. An empty cell adds a slot there; a block
/// opens it. Sessions are generated from these four weeks ahead (the server's
/// roller), so the «Календар» fills by itself.
function TimetableScreen({
  ctx,
  open,
  setOpen,
}: {
  ctx: ServicesContext
  open: SlotOpen | null
  setOpen: (next: SlotOpen | null) => void
}) {
  const { orgId, can } = ctx
  const f = t.services.timetable
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const now = useNow(60_000)
  const branchId = search.branch && ctx.branches.some((b) => b.id === search.branch) ? search.branch : null
  const zone = ctx.zoneOf(branchId)
  const today = wallOf(now, zone).date

  const templates = useQuery(templatesQuery(orgId))
  const types = useQuery(classTypesQuery(orgId))
  const resources = useQuery({ ...resourcesQuery(orgId), enabled: can.manageTimetable })
  const retire = useRetireTemplate(orgId)
  const [retiring, setRetiring] = useState<TemplateRow | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const groupServices = (types.data ?? []).filter((x) => x.kind === 'group' && x.status === 'active')
  const photoOf = new Map(ctx.members.map((m) => [m.userId, photoSrc(m.photoUrl)]))
  const roomName = new Map((resources.data ?? []).map((room) => [room.id, room.name]))
  const { running, ended } = splitSlots(slotsOfBranch(templates.data ?? [], branchId), today)
  const trainerName = (slot: TemplateRow) =>
    slot.trainerUserId ? slot.trainer?.displayName || ctx.nameOf(slot.trainerUserId) : f.noTrainer
  const places = (slot: TemplateRow) => {
    const fallback = types.data?.find((x) => x.id === slot.classTypeId)?.defaultCapacity ?? null
    const capacity = slot.capacity ?? fallback
    return capacity !== null ? f.places(capacity) : null
  }

  // Over EVERY club's running slots: a trainer busy in another club at that
  // hour loses the class here too.
  const everyRunning = (templates.data ?? []).filter((slot) => isRunning(slot, wallOf(now, ctx.zoneOf(slot.branchId)).date))
  const clashes = slotClashes(everyRunning, ctx.zoneOf)
  const shown = new Set(running.map((slot) => slot.id))
  const pairs = clashPairs(everyRunning, clashes).filter((pair) => shown.has(pair.first.id) || shown.has(pair.second.id))
  const clashText = (slot: TemplateRow): string | null => {
    const found = clashes.get(slot.id)
    return found?.length
      ? f.clash(found.map(({ other }) => f.clashWith(other.classType.name, f.shortDays[other.weekday] ?? '', other.localStartTime.slice(0, 5))))
      : null
  }

  const items: GridItem[] = running.flatMap((slot) => {
    const name = slot.classType.name
    const who = trainerName(slot)
    const room = slot.resourceId ? roomName.get(slot.resourceId) : undefined
    const from = slot.localStartTime.slice(0, 5)
    const clash = clashText(slot)
    return slotParts(slot).map((part) => ({
      id: slotItemId(slot.id, part.part),
      startsAt: part.startsAt,
      endsAt: part.endsAt,
      title: name,
      detail: who,
      photo: slot.trainerUserId ? { name: who, src: photoOf.get(slot.trainerUserId) ?? null } : undefined,
      meta: String(slot.capacity ?? types.data?.find((x) => x.id === slot.classTypeId)?.defaultCapacity ?? ''),
      extra: [room, startsLater(slot, today) && slot.activeFrom ? f.from(shortDate(slot.activeFrom)) : null]
        .filter(Boolean)
        .join(' · ') || undefined,
      tone: 'group' as const,
      colour: slot.classType.colour,
      warning: clash ?? undefined,
      label: [f.slotLabel(name, f.days[slot.weekday] ?? '', from, endOf(from, slot.durationMinutes), who), places(slot), room, clash]
        .filter(Boolean)
        .join(', '),
    }))
  })

  const days = referenceWeek()
  // Per day's piece: a class past midnight needs the early hours shown too.
  const visible = visibleHours(running.flatMap(slotParts))
  const byId = new Map((templates.data ?? []).map((slot) => [slot.id, slot]))
  const canAdd = can.manageTimetable && groupServices.length > 0

  const done = (result: SlotDone) => {
    setOpen(null)
    const notes = [
      result.message,
      result.change && result.change.updated > 0 ? f.moved(result.change.updated) : null,
      result.change && result.change.withdrawn > 0 ? f.withdrawn(result.change.withdrawn) : null,
      result.generation && result.generation.skippedConflict > 0 ? f.conflicts(result.generation.skippedConflict) : null,
    ]
    setOutcome({ message: notes.filter(Boolean).join(' '), change: result.change, zone: result.zone })
  }

  const slotMenu = (slot: TemplateRow) =>
    can.manageTimetable ? (
      <RowMenu label={`${t.app.more}: ${slot.classType.name}`} trigger={<Ellipsis className="size-[18px]" />}>
        <RowMenuItem icon={<Pencil className="size-4" />} onSelect={() => setOpen({ slot })}>
          {f.edit}
        </RowMenuItem>
        <RowMenuItem destructive icon={<Archive className="size-4" />} onSelect={() => setRetiring(slot)}>
          {f.retire}
        </RowMenuItem>
      </RowMenu>
    ) : undefined

  const toolbar =
    ctx.branches.length > 1 ? (
      <div className="flex flex-wrap items-center gap-2">
        <BranchSelect
          branches={ctx.branches}
          value={branchId}
          all
          onChange={(id) => void navigate({ search: id ? { branch: id } : {}, replace: true })}
        />
      </div>
    ) : null

  if (templates.isPending || types.isPending) return <LoadingBlock label={t.app.loading} />
  const failed = templates.error ?? types.error
  if (failed) {
    return (
      <InlineBanner
        kind="error"
        message={errorText(failed)}
        action={{
          label: t.app.retry,
          onClick: () => {
            void templates.refetch()
            void types.refetch()
          },
        }}
      />
    )
  }

  const nothing = (templates.data ?? []).length === 0

  return (
    <div className="flex flex-col gap-6">
      {toolbar}
      {outcome ? <OutcomeCard orgId={orgId} outcome={outcome} onDismiss={() => setOutcome(null)} /> : null}
      {!can.manageTimetable ? <InlineBanner kind="info" message={can.own ? f.ownReadOnly : f.readOnly} /> : null}
      {resources.isError ? <InlineBanner kind="error" message={errorText(resources.error)} /> : null}
      {pairs.length > 0 ? (
        <InlineBanner
          kind="warning"
          title={f.clashTitle(pairs.length)}
          message={
            <span className="flex flex-col gap-1">
              {pairs.map(({ first, second, by }) => (
                <span key={`${first.id}-${second.id}`}>
                  {f.clashPair(
                    first.classType.name,
                    `${f.shortDays[first.weekday]} ${first.localStartTime.slice(0, 5)}`,
                    second.classType.name,
                    `${f.shortDays[second.weekday]} ${second.localStartTime.slice(0, 5)}`,
                    by === 'trainer'
                      ? f.clashByTrainer(trainerName(first))
                      : f.clashByRoom((first.resourceId && roomName.get(first.resourceId)) || f.room),
                  )}
                </span>
              ))}
              <span>{f.clashHint}</span>
            </span>
          }
        />
      ) : null}

      {nothing && groupServices.length === 0 ? (
        <EmptyState
          icon={<CalendarRange />}
          title={f.noGroup}
          message={f.noGroupMessage}
          action={
            can.manageCatalogue ? (
              <Link
                to="/c/$orgId/services/catalogue"
                params={{ orgId }}
                className="inline-flex h-11 items-center rounded-[12px] bg-accent px-4.5 text-callout font-semibold text-ink sm:h-10"
              >
                {t.services.catalogue.add}
              </Link>
            ) : undefined
          }
        />
      ) : nothing ? (
        <EmptyState
          icon={<CalendarRange />}
          title={f.empty}
          message={canAdd ? f.emptyMessage : f.emptyReadOnly}
          action={
            canAdd ? (
              <Button variant="primary" icon={<Plus className="size-[18px]" strokeWidth={2.5} />} onClick={() => setOpen({ at: null })}>
                {f.add}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* A desk: the week as a grid. */}
          <div className="hidden flex-col gap-2 md:flex">
            <WeekGrid
              columns={days.map((date) => ({ key: date, date, head: <DateHead date={date} weekdayOnly /> }))}
              rowPx={32}
              timeZone="UTC"
              hours={visible}
              items={items}
              today=""
              nowMinutes={-1}
              label={f.grid}
              hint={f.gridHint}
              cellLabel={(column, minutes) => (canAdd ? f.cell : f.cellReadOnly)(f.days[weekdayOf(column.date)] ?? '', hhmm(minutes))}
              onPick={canAdd ? (column, minutes) => setOpen({ at: { weekday: weekdayOf(column.date), minutes } }) : undefined}
              onOpen={(id) => {
                const slot = byId.get(slotIdOfItem(id))
                if (slot) setOpen({ slot })
              }}
            />
          </div>

          {/* A phone: the same week as a list by day. */}
          <div className="flex flex-col gap-6 md:hidden">
            {byWeekday(running).map((day) => (
              <SettingsSection key={day.weekday} title={f.days[day.weekday]} meta={String(day.slots.length)}>
                {day.slots.map((slot) => {
                  const from = slot.localStartTime.slice(0, 5)
                  const who = trainerName(slot)
                  const room = slot.resourceId ? roomName.get(slot.resourceId) : undefined
                  const clash = clashText(slot)
                  return (
                    <CompactRow
                      key={slot.id}
                      leading={<Avatar name={slot.trainerUserId ? who : ''} src={slot.trainerUserId ? (photoOf.get(slot.trainerUserId) ?? null) : null} />}
                      title={
                        clash ? (
                          <span className="flex min-w-0 items-center gap-1.5">
                            <TriangleAlert aria-label={clash} className="size-4 shrink-0 text-warning" strokeWidth={2.25} />
                            <span className="truncate">{slot.classType.name}</span>
                          </span>
                        ) : (
                          slot.classType.name
                        )
                      }
                      subtitle={[`${from}–${endOf(from, slot.durationMinutes)}`, who, room].filter(Boolean).join(' · ')}
                      secondaryValue={places(slot) ?? undefined}
                      onClick={() => setOpen({ slot })}
                      actions={slotMenu(slot)}
                    />
                  )
                })}
              </SettingsSection>
            ))}
          </div>

          {ended.length > 0 ? (
            <SettingsSection title={f.ended} meta={String(ended.length)} footer={f.endedFootnote}>
              {ended.map((slot) => (
                <CompactRow
                  key={slot.id}
                  tone="muted"
                  // Past its «Діє до» but not retired: opens, so its end can move.
                  onClick={reopenable(slot) ? () => setOpen({ slot }) : undefined}
                  actions={reopenable(slot) ? slotMenu(slot) : undefined}
                  title={slot.classType.name}
                  subtitle={[
                    `${f.shortDays[slot.weekday]} ${slot.localStartTime.slice(0, 5)}`,
                    trainerName(slot),
                    slot.activeUntil ? f.endedOn(shortDate(slot.activeUntil)) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              ))}
            </SettingsSection>
          ) : null}
        </>
      )}

      <SlotPanel
        ctx={ctx}
        open={open}
        services={groupServices}
        resources={resources.data ?? []}
        branchId={branchId}
        onClose={() => setOpen(null)}
        onDone={done}
        onRetire={(slot) => setRetiring(slot)}
      />

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRetiring(null)
            retire.reset()
          }
        }}
        title={retiring ? f.retireTitle(retiring.classType.name, f.days[retiring.weekday] ?? '', retiring.localStartTime.slice(0, 5)) : ''}
        message={f.retireMessage}
        confirmLabel={f.retireConfirm}
        busy={retire.isPending}
        error={retire.isError ? <InlineBanner kind="error" message={slotErrorText(retire.error)} /> : null}
        onConfirm={() => {
          if (!retiring) return
          const slot = retiring
          retire.mutate(
            { id: slot.id },
            {
              onSuccess: (saved) => {
                setRetiring(null)
                setOpen(null)
                setOutcome({
                  message: [
                    f.retiredBanner(slot.classType.name),
                    saved.change && saved.change.withdrawn > 0 ? f.withdrawn(saved.change.withdrawn) : null,
                  ]
                    .filter(Boolean)
                    .join(' '),
                  change: saved.change,
                  zone: ctx.zoneOf(slot.branchId),
                })
              },
            },
          )
        }}
      />
    </div>
  )
}

/// The news of the last save, and the sessions it left as they were — each a
/// link to its week in «Календар», where a class that will not happen is
/// cancelled (its people are told).
function OutcomeCard({ orgId, outcome, onDismiss }: { orgId: string; outcome: Outcome; onDismiss: () => void }) {
  const f = t.services.timetable
  const kept = keptSessions(outcome.change)
  return (
    <div className="flex flex-col gap-3">
      <InlineBanner kind="done" message={outcome.message} action={kept.length === 0 ? { label: f.dismiss, onClick: onDismiss } : undefined} />
      {kept.length > 0 ? (
        <SettingsSection
          title={f.keptTitle(kept.length)}
          footer={f.keptHint}
          meta={
            <button type="button" onClick={onDismiss} className="text-caption font-medium text-text-2 hover:text-text">
              {f.dismiss}
            </button>
          }
        >
          {kept.map((session) => (
            <Link
              key={session.sessionId}
              to="/c/$orgId/services"
              params={{ orgId }}
              search={{ view: 'day', date: wallOf(new Date(session.startsAt), outcome.zone).date }}
              className="compact-row relative flex min-h-12 items-center gap-3 px-4 hover:bg-[var(--row-hover,var(--surface-2))]"
            >
              <span className="min-w-0 flex-1 truncate text-callout text-text tabular first-letter:uppercase">
                {dayAndMonth(session.startsAt, outcome.zone)}, {hhmm(wallOf(new Date(session.startsAt), outcome.zone).minutes)}
              </span>
              <span className="shrink-0 text-caption text-text-2">
                {session.reason === 'edited' ? f.keptEdited : f.keptBooked(session.booked)}
              </span>
              <span className="sr-only">{f.openCalendar}</span>
            </Link>
          ))}
        </SettingsSection>
      ) : null}
    </div>
  )
}
